/**
 * The `qa` run's parameters and result, mirroring the backend's `QaParams` /
 * `QaResult` (services/qa_solution.py). The OpenAPI types a run result as a
 * kind-specific JSON object, so the shape is declared here.
 */

import type { Citation } from "@/features/solutions/shared";

/** One confirmed question — an item of the run's `params.questions`. */
export interface QaQuestion {
  id: string;
  question: string;
  source_location?: string | null;
}

export type QaStatus = "answered" | "not_found" | "error";

export interface QaAnswer {
  id: string;
  question: string;
  source_location: string | null;
  status: QaStatus;
  answer: string;
  grounded: boolean;
  citations: Citation[];
  ai_inference_id: string;
  error: string | null;
}

/** The model that extracted and answered (`QaModel`). `model` / `model_version`
 *  are as configured; `model_reported` is what the service said answered. */
export interface QaModel {
  platform: boolean;
  connection_id: string;
  connection_name: string;
  deployment: string;
  model: string | null;
  model_version: string | null;
  model_reported: string | null;
}

/** "Connection · deployment (reported model)", falling back to the configured
 *  model + version — the same wording as the PDF and DOCX headers. */
export function answeredByLabel(m: QaModel): string {
  const detail = m.model_reported ?? [m.model, m.model_version].filter(Boolean).join(" ");
  const head = `${m.connection_name} · ${m.deployment}`;
  return detail ? `${head} (${detail})` : head;
}

export interface QaResult {
  answers: QaAnswer[];
  answered: number;
  not_found: number;
  failed: number;
  questionnaire: string | null;
  /** Output name for `downloadRunOutput` — the editable DOCX. */
  docx_output: string | null;
  /** Absent on runs made before the model pick existed. */
  model?: QaModel | null;
}

export const QA_STATUS_LABEL: Record<QaStatus, string> = {
  answered: "Answered",
  not_found: "Not found in documents",
  error: "Failed",
};

export const QA_STATUS_COLOR: Record<QaStatus, string> = {
  answered: "green",
  not_found: "orange",
  error: "red",
};

/** The run result as a `QaResult`, or `null` when it is not one (yet). */
export function asQaResult(result: unknown): QaResult | null {
  if (!result || typeof result !== "object") return null;
  const r = result as Partial<QaResult>;
  return Array.isArray(r.answers) ? (r as QaResult) : null;
}

/** An editable row of the step-1 table. `key` is stable across edits. */
export interface DraftQuestion {
  key: string;
  question: string;
  source_location: string | null;
  /** `false` = the server could not find this text in the document. */
  verbatim: boolean;
  /** Added by the user rather than extracted. */
  added: boolean;
}

/** The confirmed list, numbered `Q1…` in the order the user left it; blank
 *  rows dropped. */
export function toRunQuestions(rows: DraftQuestion[]): QaQuestion[] {
  return rows
    .filter((r) => r.question.trim())
    .map((r, i) => ({
      id: `Q${i + 1}`,
      question: r.question.trim(),
      source_location: r.source_location,
    }));
}

/** Move the item at `from` to `to` (clamped); returns a new array. */
export function move<T>(items: T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length || from === to) return items;
  const next = items.slice();
  const [item] = next.splice(from, 1);
  if (item !== undefined) next.splice(to, 0, item);
  return next;
}

let addedRows = 0;

/** A blank row the user adds to the step-1 table. */
export function newDraftRow(): DraftQuestion {
  addedRows += 1;
  return {
    key: `added-${Date.now()}-${addedRows}`,
    question: "",
    source_location: null,
    verbatim: true,
    added: true,
  };
}
